import TextField from "@mui/material/TextField"
import Box from "@mui/material/Box"
import InputAdornment from "@mui/material/InputAdornment"
import Select from "@mui/material/Select"
import MenuItem from "@mui/material/MenuItem"
import Switch from "@mui/material/Switch"
import FormControlLabel from "@mui/material/FormControlLabel"
import { useForm, Controller, SubmitHandler } from "react-hook-form"
import {
    SellElectricityType,
    type SellElectricityFormInput,
    SellElectricity,
} from "../../types"
import CustomButton from "../custom-button"
import { useCreateSellElectricity, useUpdateSellElectricity } from "../../hooks/use-sell-electricity"
import { SELL_ELECTRICITY_TYPES } from "../../constants"
import { convertFromKiloWattHoursPrice } from "../../helpers"
import { BottomButtons, FormControlWithLabelWrapper } from "@/views/wizard/steps/wizard-common"
import { PreviousStepButton } from "@/views/wizard/components"

const SELL_ELECTRICITY_TYPE_LABEL_MAP = {
    [SellElectricityType.flat]: 'Prodávám za pevnou částku za MWh',
    [SellElectricityType.spot]: 'Prodávám dle spotových cen',
    [SellElectricityType.null]: 'Neprodávám',
}

export default function WizardSellElectricityForm({
    estateId,
    sellElectricity,
    goBack,
    nextStep,
}: {
    estateId: string | number | undefined,
    sellElectricity?: SellElectricity,
    goBack: () => void,
    nextStep: () => void,
}) {
    const sell_fees_amount_cents = sellElectricity?.sell_fees.length > 0 ?
        sellElectricity.sell_fees[sellElectricity.sell_fees.length - 1].amount_cents : null
    
    const sell_electricity_amount_cents = sellElectricity?.sell_electricity.length > 0 ?
        sellElectricity.sell_electricity[sellElectricity.sell_electricity.length - 1].amount_cents : null
    
    const { control, watch, handleSubmit } = useForm({
        defaultValues: {
            type: sellElectricity?.type ?? SellElectricityType.null,
            sell_fees_amount_cents: sell_fees_amount_cents ? convertFromKiloWattHoursPrice(sell_fees_amount_cents) : '',
            sell_amount_cents: sell_electricity_amount_cents ?  convertFromKiloWattHoursPrice(sell_electricity_amount_cents) : '',
            cant_be_negative: sellElectricity.cant_be_negative ?? false,
        },
    })

    const mutationUpdate = useUpdateSellElectricity(sellElectricity.id, estateId, () => nextStep())

    const mutationCreate = useCreateSellElectricity(estateId, () => nextStep())

    const onSubmit: SubmitHandler<SellElectricityFormInput> = data => {
        if (sellElectricity?.id) {
            mutationUpdate.mutate(data)
        } else {
            mutationCreate.mutate(data)
        }
    }

    const typeWatch = watch('type')

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 5,
            }}>
                <FormControlWithLabelWrapper
                    label="Prodáváte elektřinu?"
                    labelId='sellElectricity-type'
                >
                    <Controller
                        name="type"
                        control={control}
                        render={({ field }) =>
                            <Select
                                labelId="sell-electricity-type-label"
                                id="sell-electricity-type"
                                {...field}
                            >
                                {SELL_ELECTRICITY_TYPES.map(sellElType => (
                                    <MenuItem
                                        key={sellElType.id}
                                        value={sellElType.id}
                                    >
                                        {SELL_ELECTRICITY_TYPE_LABEL_MAP[sellElType.id]}
                                    </MenuItem>
                                ))}
                            </Select>
                        }
                    />
                </FormControlWithLabelWrapper>
                {typeWatch !== SellElectricityType.null &&
                    <FormControlWithLabelWrapper
                        label={'Cena za službu obchodu'}
                        labelId='sell-electricity-fees'
                    >
                        <Controller
                            name="sell_fees_amount_cents"
                            control={control}
                            render={({ field }) =>
                                <TextField
                                    id="sell-electricity-fees"
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">Kč bez DPH/MWh</InputAdornment>,
                                    }}
                                    variant="standard"
                                    type="number"
                                    required
                                    {...field}
                                />}
                        />
                    </FormControlWithLabelWrapper>
                }
                {typeWatch === SellElectricityType.flat &&
                    <FormControlWithLabelWrapper
                        label={'Cena za elektřinu'}
                        labelId='sell-electricity-amount'
                    >
                        <Controller
                            name="sell_amount_cents"
                            control={control}
                            render={({ field }) =>
                                <TextField
                                    id="sell-electricity-amount"
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">Kč bez DPH/MWh</InputAdornment>,
                                    }}
                                    variant="standard"
                                    type="number"
                                    required
                                    {...field}
                                />}
                        />
                    </FormControlWithLabelWrapper>
                }
                {typeWatch === SellElectricityType.spot &&
                    <Box mt={1}>
                        <FormControlLabel control={
                            <Controller
                            name="cant_be_negative"
                            control={control}
                            render={({ field }) =>
                                <Switch
                                    onChange={(e) => field.onChange(e.target.checked)}
                                    checked={field.value}
                                />
                            }
                        />
                        } label="Prodejní cena nemůže být záporná" />
                    </Box>
                }
                {mutationUpdate.isError ? (
                    <div>Došlo k chybě: {mutationUpdate.error.message}</div>
                ) : null}
                {mutationCreate.isError ? (
                    <div>Došlo k chybě: {mutationCreate.error.message}</div>
                ) : null}
                <Box pt={2}>
                    <BottomButtons>
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                            }}>
                                <PreviousStepButton
                                    onClick={() => goBack()}
                                >
                                    Zpět
                                </PreviousStepButton>

                                <CustomButton
                                    text="Pokračovat"
                                    type="submit"
                                    disabled={mutationUpdate.isPending || mutationCreate.isPending}
                                />
                            </Box>
                        </Box>
                    </BottomButtons>
                </Box>
            </Box>
        </form>
    )
}
